import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import BatchQueryModal from '@/shein-platform/modules/products/components/BatchQueryModal';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import {
  joinBatchTokens,
  parseBatchTokens,
} from '@/shein-platform/modules/products/components/batchQueryUtils';
import { useSheinPageShopScope, stackShopDimensionTabs } from '@/shein-platform/hooks/useSheinPageShopScope';
import { readNoticeSkc } from '@/shein-platform/utils/sheinNoticeFocusQuery';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import {
  RRP_SKC_STATUS_LABELS,
} from '@/shein-platform/constants/sheinApiCatalog';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import {
  applyRrpAutoFill,
  collectRrpProofMaterials,
  extractRrpAuditList,
  extractRrpSkcList,
  extractRrpSkcTotal,
  formatAuditTime,
  formatMsTime,
  formatRrpMoney,
  isRrpDemoSkc,
  mergeRrpSkcEditRow,
  pickRrpBrandCode,
  pickRrpCategory,
  pickRrpProductImage,
  pickRrpProductTitle,
  pickRrpSkcName,
  rrpAuditModifier,
  rrpAuditStateLabel,
  rrpMaterialRequirementLabel,
  rrpSkcStatusLabel,
  rrpSkcStatusModifier,
  summarizeSkuRrpInfoList,
} from '@/shein-platform/modules/discuss/rrpDisplay';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import { rememberRrpPendingForHome } from '@/shein-platform/utils/sheinHomeLocalSnapshot';
import {
  filterTemplatesByProductCategory,
  pickAutoRuleTemplate,
} from '@/shein-platform/utils/sheinTemplateAutoRule';
import RrpProofUploadBlock from '@/shein-platform/modules/discuss/RrpProofUploadBlock';
import RrpAutoRuleModal from '@/shein-platform/modules/discuss/RrpAutoRuleModal';
import { showToast } from '@/utils/toast';
import '@/styles/shein-platform/shein-product-ops.css';

function pickRrpCategoryMeta(skcRow) {
  return {
    productTypeId: String(skcRow?.product_type_id || skcRow?.productTypeId || '').trim(),
    categoryId: String(skcRow?.category_id || skcRow?.categoryId || '').trim(),
    categoryPathLabel: String(
      pickRrpCategory(skcRow)
      || skcRow?.category_path_label
      || skcRow?.categoryPathLabel
      || '',
    ).trim(),
  };
}

const SKC_STATUS_TABS = [
  { value: '', label: '全部' },
  ...Object.entries(RRP_SKC_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

const RRP_BATCH_QUERY_LIMIT = 50;

function buildRrpEditPriceRows(skcRow) {
  const skuList = Array.isArray(skcRow?.sku_rrp_info_list)
    ? skcRow.sku_rrp_info_list
    : (Array.isArray(skcRow?.skuRrpInfoList) ? skcRow.skuRrpInfoList : []);
  const next = [];
  for (const sku of skuList) {
    const sites = Array.isArray(sku?.site_rrp_info_list)
      ? sku.site_rrp_info_list
      : (Array.isArray(sku?.siteRrpInfoList) ? sku.siteRrpInfoList : []);
    for (const site of sites) {
      next.push({
        sku_code: sku.sku_code || sku.skuCode || '',
        site: site.site || '',
        currency: site.currency || '',
        suggested_retail_price: site.suggested_retail_price != null ? String(site.suggested_retail_price) : '',
        type_one_attachments: Array.isArray(site?.type_one_proof?.attachments)
          ? [...site.type_one_proof.attachments]
          : (Array.isArray(site?.typeOneProof?.attachments) ? [...site.typeOneProof.attachments] : []),
        type_one_link: site?.type_one_proof?.proof_link || site?.typeOneProof?.proof_link || '',
        type_two_attachments: Array.isArray(site?.type_two_proof?.attachments)
          ? [...site.type_two_proof.attachments]
          : (Array.isArray(site?.typeTwoProof?.attachments) ? [...site.typeTwoProof.attachments] : []),
        type_two_link: site?.type_two_proof?.proof_link || site?.typeTwoProof?.proof_link || '',
      });
    }
  }
  return next;
}

function RrpEditModal({
  open,
  shopId,
  skcRow,
  rule,
  busy,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation('workbench');
  const [rows, setRows] = useState([]);
  const autoFilledRef = useRef(false);
  const skcNameKey = pickRrpSkcName(skcRow);

  // 仅在打开/切换 SKC 时重建价格行，避免后台 live 刷新冲掉用户已填内容
  useEffect(() => {
    if (!open || !skcRow) return;
    autoFilledRef.current = false;
    setRows(buildRrpEditPriceRows(skcRow));
  }, [open, skcNameKey]);

  // 缓存打开时可能暂无价格行：后台 live 补齐后自动填入（已有行则保留）
  useEffect(() => {
    if (!open || !skcRow) return;
    setRows((prev) => {
      if (prev.length > 0) return prev;
      const built = buildRrpEditPriceRows(skcRow);
      return built.length > 0 ? built : prev;
    });
  }, [open, skcRow]);

  useEffect(() => {
    if (!open || !skcRow || !shopId) return undefined;
    if (autoFilledRef.current) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const meta = pickRrpCategoryMeta(skcRow);
        const listRes = await sheinPlatformAPI.listRrpTemplates(shopId, {
          productTypeId: meta.productTypeId || undefined,
          page: 1,
          pageSize: 50,
        });
        let items = Array.isArray(listRes?.items) ? listRes.items : [];
        if (!items.length && meta.productTypeId) {
          const allRes = await sheinPlatformAPI.listRrpTemplates(shopId, { page: 1, pageSize: 50 });
          items = Array.isArray(allRes?.items) ? allRes.items : [];
        }
        if (cancelled) return;
        const picked = pickAutoRuleTemplate(filterTemplatesByProductCategory(items, meta));
        if (!picked?.id) return;
        let template = picked;
        try {
          const detailRes = await sheinPlatformAPI.getRrpTemplate(shopId, picked.id);
          template = detailRes?.item || detailRes || picked;
        } catch {
          // 列表项无 payload 时仍可用顶层 markupPercent
        }
        if (cancelled) return;
        autoFilledRef.current = true;
        setRows((prev) => {
          const { rows: next, filled } = applyRrpAutoFill(prev, template);
          if (filled) {
            const label = template.name || picked.name || t('productTemplate.templateFallback');
            setTimeout(() => {
              showToast(t('rrpOps.filledFrom', { name: label }), 'success');
            }, 0);
            return next;
          }
          return prev;
        });
      } catch {
        // 自动填充失败静默跳过
      }
    })();
    return () => { cancelled = true; };
  }, [open, shopId, skcRow, t]);

  if (!open || !skcRow) return null;

  const editable = Number(skcRow.status) !== 1;
  const siteRule = Number(rule?.site_filling_rule) === 1;
  const availableSites = Array.isArray(rule?.available_sites) ? rule.available_sites : [];
  const materialReq = rule?.material_requirement;
  const rrpRequired = Number(rule?.is_rrp_required) === 1;
  const productTitle = pickRrpProductTitle(skcRow);
  const productImage = pickRrpProductImage(skcRow);
  const category = pickRrpCategory(skcRow);
  const brandCode = pickRrpBrandCode(skcRow);

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const submit = () => {
    if (!editable) {
      showToast(skcRow.status_desc || t('rrpOps.auditingLocked'), 'warning');
      return;
    }
    const skuMap = new Map();
    for (const r of rows) {
      if (!r.sku_code) continue;
      const price = String(r.suggested_retail_price || '').trim();
      const item = {
        sku_code: r.sku_code,
        currency: r.currency || undefined,
        site: r.site || undefined,
        suggested_retail_price: price === '' ? undefined : Number(price),
        type_one_proof: {
          attachments: (r.type_one_attachments || []).slice(0, 6),
          proof_link: String(r.type_one_link || '').trim().slice(0, 1000),
        },
        type_two_proof: {
          attachments: (r.type_two_attachments || []).slice(0, 6),
          proof_link: String(r.type_two_link || '').trim().slice(0, 1000),
        },
      };
      if (!skuMap.has(r.sku_code)) skuMap.set(r.sku_code, []);
      skuMap.get(r.sku_code).push(item);
    }
    const sku_rrp_info_list = [];
    for (const list of skuMap.values()) {
      sku_rrp_info_list.push(...list);
    }
    if (!sku_rrp_info_list.length) {
      showToast(t('rrpOps.noSkuRows'), 'warning');
      return;
    }
    onSubmit({
      skc_rrp_list: [{
        skc_name: skcRow.skc_name,
        sku_rrp_info_list,
      }],
      brandCode,
    });
  };

  return (
    <div className="spo-batch-modal" role="presentation" onClick={onClose}>
      <div className="spo-batch-modal__backdrop" aria-hidden="true" />
      <div
        className="spo-batch-modal__panel spo-discuss-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-rrp-edit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="spo-batch-modal__head">
          <h2 id="spo-rrp-edit-title">{t('rrpOps.editRrp')}</h2>
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onClose}>{t('moduleShell.close')}</button>
        </header>
        <div className="spo-batch-modal__body">
          <div className="spo-discuss-requote">
            <section className="spo-discuss-requote__row" aria-label={t('rrpOps.productInfo')}>
              <div className="spo-discuss-requote__row-label">{t('rrpOps.productInfo')}</div>
              <div className="spo-return-product spo-discuss-requote__product-cell">
                {productImage ? (
                  <SheinProductThumb
                    imageUrl={productImage}
                    className="spo-return-product__img"
                    placeholderClassName="spo-return-product__img spo-return-product__img--empty"
                    alt={productTitle}
                  />
                ) : (
                  <div className="spo-return-product__img spo-return-product__img--empty" />
                )}
                <div className="spo-return-product__meta">
                  <HoverCopyField
                    value={productTitle || ''}
                    className="spo-return-product__title"
                    popoverCopy
                  />
                  <div className="spo-return-product__sku">SKC：{skcRow.skc_name || '—'}</div>
                  <div className="spo-return-product__sku">
                    {t('rrpOps.categoryLine', { name: category || '—' })}
                    {skcRow.sale_attribute_value ? ` · ${skcRow.sale_attribute_value}` : ''}
                  </div>
                </div>
              </div>
            </section>

            <section className="spo-discuss-requote__row" aria-label={t('rrpOps.statusInfo')}>
              <div className="spo-discuss-requote__row-label">{t('rrpOps.statusInfo')}</div>
              <div className="spo-rrp-edit-status">
                <div className="spo-rrp-edit-status__item">
                  <span>{t('rrpOps.status')}</span>
                  <strong>
                    {rrpSkcStatusLabel(skcRow.status)}
                    {skcRow.status_desc ? ` · ${skcRow.status_desc}` : ''}
                  </strong>
                </div>
                <div className="spo-rrp-edit-status__item">
                  <span>{t('rrpOps.validity')}</span>
                  <strong>
                    {formatMsTime(skcRow.start_effective_time)} ~ {formatMsTime(skcRow.end_effective_time)}
                  </strong>
                </div>
                <div className="spo-rrp-edit-status__item">
                  <span>{t('rrpOps.updatedAt')}</span>
                  <strong>{formatMsTime(skcRow.latest_update_time || skcRow.last_update_time)}</strong>
                </div>
              </div>
            </section>

            <section className="spo-discuss-requote__row" aria-label={t('rrpOps.brandRule')}>
              <div className="spo-discuss-requote__row-label">{t('rrpOps.brandRule')}</div>
              <div className="spo-rrp-edit-rules">
                <div className="spo-rrp-edit-brand">
                  <span className="spo-rrp-edit-brand__label">{t('rrpOps.brand')}</span>
                  <strong>{brandCode || t('rrpOps.noBrand')}</strong>
                </div>
                {rule ? (
                  <div className="spo-rrp-rule-box">
                    <div>{t('rrpOps.fillRule', { mode: rrpRequired ? t('rrpOps.required') : t('rrpOps.optional') })}</div>
                    <div>{siteRule ? t('rrpOps.siteSplit') : t('rrpOps.siteShared')}</div>
                    <div>{t('rrpOps.materialNeed', { text: [0, 1, 2].includes(Number(materialReq)) ? t('rrpOps.material' + Number(materialReq)) : rrpMaterialRequirementLabel(materialReq) })}</div>
                    {siteRule && availableSites.length ? (
                      <div>{t('rrpOps.sitesLine', { text: availableSites.map((s) => `${s.site}/${s.currency}`).join('、') })}</div>
                    ) : null}
                  </div>
                ) : (
                  <p className="spo-muted">{t('rrpOps.noRule')}</p>
                )}
                <p className="spo-batch-modal__hint spo-rrp-edit-hint">
                  {t('rrpOps.editHint')}
                </p>
              </div>
            </section>

            <section className="spo-discuss-requote__row" aria-label={t('rrpOps.priceInfo')}>
              <div className="spo-discuss-requote__row-label">{t('rrpOps.priceInfo')}</div>
              <div className="spo-rrp-edit-prices">
                {!rows.length ? (
                  <p className="spo-muted">{t('rrpOps.noSkuPrice')}</p>
                ) : (
                  rows.map((r, idx) => (
                    <div key={`${r.sku_code}-${r.site}-${idx}`} className="spo-rrp-edit-price-card">
                      <div className="spo-rrp-edit-price-card__head">
                        <span>SKU：{r.sku_code || '—'}</span>
                        {r.site ? <span>{t('rrpOps.siteLine', { site: r.site })}</span> : null}
                        <span>{t('rrpOps.currencyLine', { currency: r.currency || '—' })}</span>
                      </div>
                      <label className="spo-discuss-field spo-discuss-field--flush">
                        <span>{t('rrpOps.adjustedPrice')}<span className="spo-discuss-requote__optional">{t('rrpOps.clearOptional')}</span></span>
                        <div className="spo-rrp-edit-price-row">
                          <input
                            className="spo-input"
                            disabled={!editable || busy}
                            value={r.currency}
                            onChange={(e) => updateRow(idx, { currency: e.target.value })}
                            placeholder={t('rrpOps.currencyPh')}
                          />
                          <input
                            className="spo-input"
                            disabled={!editable || busy}
                            value={r.suggested_retail_price}
                            onChange={(e) => updateRow(idx, { suggested_retail_price: e.target.value })}
                            placeholder={t('rrpOps.rrpPh')}
                            inputMode="decimal"
                          />
                        </div>
                      </label>
                      <RrpProofUploadBlock
                        title={t('rrpLib.proofType1')}
                        shopId={shopId}
                        busy={!editable || busy}
                        attachments={r.type_one_attachments}
                        proofLink={r.type_one_link}
                        onChange={({ attachments, proofLink }) => updateRow(idx, {
                          type_one_attachments: attachments,
                          type_one_link: proofLink,
                        })}
                        linkPlaceholder={t('rrpOps.proofLink1')}
                      />
                      <RrpProofUploadBlock
                        title={t('rrpLib.proofType2')}
                        shopId={shopId}
                        busy={!editable || busy}
                        attachments={r.type_two_attachments}
                        proofLink={r.type_two_link}
                        onChange={({ attachments, proofLink }) => updateRow(idx, {
                          type_two_attachments: attachments,
                          type_two_link: proofLink,
                        })}
                        linkPlaceholder={t('rrpOps.proofLink2')}
                      />
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        </div>
        <footer className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--secondary" disabled={busy} onClick={onClose}>{t('shop.cancel')}</button>
          <button type="button" className="spo-btn spo-btn--primary" disabled={busy || !editable} onClick={submit}>
            {busy ? t('returnList.submitting') : t('rrpOps.submitReview')}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ProofBlock({ title, proof }) {
  const { t } = useTranslation('workbench');
  const attachments = Array.isArray(proof?.attachments) ? proof.attachments : [];
  const link = String(proof?.proof_link || '').trim();
  const empty = attachments.length === 0 && !link;
  return (
    <div className="spo-rrp-proof-view__block">
      <h4 className="spo-rrp-proof-view__type">{title}</h4>
      {empty ? (
        <p className="spo-muted">{t('rrpOps.none')}</p>
      ) : (
        <>
          {attachments.length > 0 ? (
            <div className="spo-rrp-proof-view__files">
              <span className="spo-rrp-proof-view__label">{t('rrpOps.attachment')}</span>
              <ul>
                {attachments.map((url) => (
                  <li key={url}>
                    <a href={url} target="_blank" rel="noreferrer" className="spo-rrp-proof-view__a">
                      {url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="spo-rrp-proof-view__files">
              <span className="spo-rrp-proof-view__label">{t('rrpOps.attachment')}</span>
              <span className="spo-muted">{t('rrpOps.none')}</span>
            </div>
          )}
          <div className="spo-rrp-proof-view__link">
            <span className="spo-rrp-proof-view__label">{t('rrpOps.proofLink')}</span>
            {link ? (
              <a href={link} target="_blank" rel="noreferrer" className="spo-rrp-proof-view__a">
                {link}
              </a>
            ) : (
              <span className="spo-muted">{t('rrpOps.none')}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function RrpProofModal({ open, skc, items, onClose }) {
  const { t } = useTranslation('workbench');
  if (!open) return null;
  return (
    <div className="spo-batch-modal" role="presentation" onClick={onClose}>
      <div className="spo-batch-modal__backdrop" aria-hidden="true" />
      <div
        className="spo-batch-modal__panel spo-discuss-modal__panel spo-rrp-proof-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-rrp-proof-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="spo-batch-modal__head">
          <h2 id="spo-rrp-proof-title">{t('rrpOps.proofInfoTitle')}{skc ? ` · ${skc}` : ''}</h2>
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </header>
        <div className="spo-batch-modal__body">
          {!items?.length ? (
            <p className="spo-muted">{t('rrpOps.noProof')}</p>
          ) : (
            <div className="spo-rrp-proof-view">
              {items.map((item) => (
                <section key={`${item.sku_code}-${item.site}`} className="spo-rrp-proof-view__site">
                  <div className="spo-rrp-proof-view__meta">
                    <span>SKU：{item.sku_code}</span>
                    <span>{t('rrpOps.siteLine', { site: item.site })}</span>
                  </div>
                  <ProofBlock title={t('rrpLib.proofType1')} proof={item.type_one_proof} />
                  <ProofBlock title={t('rrpLib.proofType2')} proof={item.type_two_proof} />
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AuditSkuLines({ skuList }) {
  const { t } = useTranslation('workbench');
  const list = Array.isArray(skuList) ? skuList : [];
  if (!list.length) {
    return <p className="spo-muted">{t('rrpOps.noSkuAudit')}</p>;
  }
  return (
    <div className="spo-rrp-audit-skus">
      {list.map((sku, idx) => {
        const site = String(sku?.site || '').trim();
        const before = sku?.before_suggested_retail_price ?? sku?.beforeSuggestedRetailPrice;
        const after = sku?.suggested_retail_price ?? sku?.suggestedRetailPrice;
        const currency = sku?.currency || '';
        return (
          <div key={`${sku?.sku_code || idx}-${site}-${idx}`} className="spo-rrp-audit-sku-card">
            <div className="spo-rrp-audit-sku-card__head">
              <span>{t('rrpOps.skuLine', { code: sku?.sku_code || sku?.skuCode || '—' })}</span>
              {site ? <span>{t('rrpOps.siteLine', { site })}</span> : null}
              <span className={`spo-records__status spo-records__status--${rrpAuditModifier(sku?.audit_state)}`}>
                {t('rrpOps.audit' + Number(sku?.audit_state), { defaultValue: rrpAuditStateLabel(sku?.audit_state) })}
              </span>
            </div>
            <div className="spo-rrp-edit-status spo-rrp-audit-sku-card__grid">
              <div className="spo-rrp-edit-status__item">
                <span>{t('rrpOps.beforeRrp')}</span>
                <strong>{formatRrpMoney(before, currency)}</strong>
              </div>
              <div className="spo-rrp-edit-status__item">
                <span>{t('rrpOps.auditRrp')}</span>
                <strong>{formatRrpMoney(after, currency)}</strong>
              </div>
              <div className="spo-rrp-edit-status__item">
                <span>{t('rrpOps.skuReject')}</span>
                <strong>{sku?.audit_reason || sku?.auditReason || '—'}</strong>
              </div>
            </div>
            <ProofBlock title={t('rrpLib.proofType1')} proof={sku?.type_one_proof || sku?.typeOneProof} />
            <ProofBlock title={t('rrpLib.proofType2')} proof={sku?.type_two_proof || sku?.typeTwoProof} />
          </div>
        );
      })}
    </div>
  );
}

function RrpAuditDetailModal({ open, skc, listRow, rows, onClose }) {
  const { t } = useTranslation('workbench');
  if (!open) return null;
  const productTitle = pickRrpProductTitle(listRow) || pickRrpProductTitle(rows?.[0]) || skc || '—';
  const productImage = pickRrpProductImage(listRow) || pickRrpProductImage(rows?.[0]);
  const category = pickRrpCategory(listRow) || pickRrpCategory(rows?.[0]);

  return (
    <div className="spo-batch-modal" role="presentation" onClick={onClose}>
      <div className="spo-batch-modal__backdrop" aria-hidden="true" />
      <div
        className="spo-batch-modal__panel spo-discuss-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-rrp-audit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="spo-batch-modal__head">
          <h2 id="spo-rrp-audit-title">{t('rrpOps.auditTitle')}{skc ? ` · ${skc}` : ''}</h2>
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </header>
        <div className="spo-batch-modal__body">
          {!rows?.length ? (
            <div className="spo-certs-empty">
              <p className="spo-certs-empty__title">{t('rrpOps.noAudit')}</p>
            </div>
          ) : (
            <div className="spo-rrp-audit-list">
              {rows.map((row, rowIdx) => {
                const skcName = row.skc_name || row.skcName || skc || '—';
                const spuName = row.spu_name || row.spuName || listRow?.spu_name || '—';
                const saleAttr = row.sale_attribute_value || row.saleAttributeValue || listRow?.sale_attribute_value || '';
                return (
                  <article
                    key={row.change_sn || row.changeSn || `${skcName}-${row.submit_time || rowIdx}`}
                    className="spo-discuss-requote spo-rrp-audit-record"
                  >
                    <section className="spo-discuss-requote__row" aria-label={t('rrpOps.productInfo')}>
                      <div className="spo-discuss-requote__row-label">{t('rrpOps.productInfo')}</div>
                      <div className="spo-return-product spo-discuss-requote__product-cell">
                        {productImage ? (
                          <SheinProductThumb
                            imageUrl={productImage}
                            className="spo-return-product__img"
                            placeholderClassName="spo-return-product__img spo-return-product__img--empty"
                            alt={productTitle}
                          />
                        ) : (
                          <div className="spo-return-product__img spo-return-product__img--empty" />
                        )}
                        <div className="spo-return-product__meta">
                          <HoverCopyField
                            value={productTitle || ''}
                            className="spo-return-product__title"
                            popoverCopy
                          />
                          <div className="spo-return-product__sku">SKC：{skcName}</div>
                          <div className="spo-return-product__sku">SPU：{spuName}</div>
                          <div className="spo-return-product__sku">
                            {t('rrpOps.saleAttr', { text: saleAttr || '—' })}
                            {category ? ` · ${category}` : ''}
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="spo-discuss-requote__row" aria-label={t('rrpOps.adjustInfo')}>
                      <div className="spo-discuss-requote__row-label">{t('rrpOps.adjustInfo')}</div>
                      <div className="spo-rrp-edit-status">
                        <div className="spo-rrp-edit-status__item">
                          <span>{t('rrpOps.orderNo')}</span>
                          <strong>{row.change_sn || row.changeSn || '—'}</strong>
                        </div>
                        <div className="spo-rrp-edit-status__item">
                          <span>{t('rrpOps.applicant')}</span>
                          <strong>{row.creator || '—'}</strong>
                        </div>
                        <div className="spo-rrp-edit-status__item">
                          <span>{t('rrpOps.submittedAt')}</span>
                          <strong>{formatAuditTime(row.submit_time || row.submitTime)}</strong>
                        </div>
                        <div className="spo-rrp-edit-status__item">
                          <span>{t('rrpOps.auditedAt')}</span>
                          <strong>{formatAuditTime(row.audit_time || row.auditTime)}</strong>
                        </div>
                      </div>
                    </section>

                    <section className="spo-discuss-requote__row" aria-label={t('rrpOps.auditStatus')}>
                      <div className="spo-discuss-requote__row-label">{t('rrpOps.auditStatus')}</div>
                      <div className="spo-rrp-edit-status">
                        <div className="spo-rrp-edit-status__item">
                          <span>{t('rrpOps.overallAudit')}</span>
                          <strong>
                            <span className={`spo-records__status spo-records__status--${rrpAuditModifier(row.audit_state)}`}>
                              {t('rrpOps.audit' + Number(row.audit_state ?? row.auditState), { defaultValue: rrpAuditStateLabel(row.audit_state ?? row.auditState) })}
                            </span>
                          </strong>
                        </div>
                        <div className="spo-rrp-edit-status__item spo-rrp-edit-status__item--wide">
                          <span>{t('rrpOps.rejectWithdraw')}</span>
                          <strong>{row.audit_reason || row.auditReason || '—'}</strong>
                        </div>
                      </div>
                    </section>

                    <section className="spo-discuss-requote__row" aria-label={t('rrpOps.noSkuAudit')}>
                      <div className="spo-discuss-requote__row-label">{t('rrpOps.skuDetail')}</div>
                      <AuditSkuLines skuList={row.sku_rrp_info_list || row.skuRrpInfoList} />
                    </section>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RrpListPage() {
  const { t } = useTranslation('workbench');
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    activeShop,
    activeShopId,
    loading: shopLoading,
    concreteScopeId,
    allShopsScope,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const shopId = concreteScopeId || (allShopsScope ? '' : (activeShop?.id || activeShopId));
  const caps = sheinShopCapabilities(activeShop);
  const canDiscuss = Boolean(caps.canDiscussCost);
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  const statusFilter = searchParams.get('status') || '';

  const [filterDraft, setFilterDraft] = useState({ skc: '' });
  const [query, setQuery] = useState({ skc: '', _nonce: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [rule, setRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [batchModalField, setBatchModalField] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [auditDetail, setAuditDetail] = useState(null);
  const [proofModal, setProofModal] = useState(null);
  const [autoRuleModalOpen, setAutoRuleModalOpen] = useState(false);
  const editSessionRef = useRef(0);

  useEffect(() => {
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
    setRows([]);
    setTotal(0);
    setError('');
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
    setLastSyncedAt('');
    setPersistedSyncedAt('');
  }, [shopId]);

  const noticeSkc = readNoticeSkc(searchParams);
  useEffect(() => {
    if (!noticeSkc) return;
    setFilterDraft({ skc: noticeSkc });
    setQuery((prev) => ({ skc: noticeSkc, _nonce: (prev._nonce || 0) + 1 }));
    setPage(1);
  }, [noticeSkc, shopId]);

  useEffect(() => {
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [query._nonce, statusFilter]);

  useEffect(() => {
    if (!shopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getShopSyncStatus(shopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'RRP_SKC');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [shopId]);

  const refreshHomePending = useCallback(async () => {
    if (!shopId) return;
    try {
      const res = await sheinPlatformAPI.getRecommendRetailPendingCount(shopId);
      rememberRrpPendingForHome(shopId, res || {});
    } catch {
      /* ignore */
    }
  }, [shopId]);

  const load = useCallback(async () => {
    if (!shopId) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const skcTokens = parseBatchTokens(query.skc).slice(0, RRP_BATCH_QUERY_LIMIT);
      const body = {
        source: 'cached',
        page_num: page,
        page_size: pageSize,
        skcNameList: skcTokens,
      };
      if (statusFilter !== '' && statusFilter != null) {
        body.status = Number(statusFilter);
      }
      const res = await sheinPlatformAPI.searchRecommendRetailPrice(shopId, body);
      const list = extractRrpSkcList(res);
      const totalCount = extractRrpSkcTotal(res, list.length);
      setRows(list);
      setTotal(totalCount);
      if (!skcTokens.length) {
        await refreshHomePending();
      }
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e?.message || t('rrpOps.loadRrpFailed'));
    } finally {
      setLoading(false);
    }
  }, [shopId, page, pageSize, statusFilter, query, refreshHomePending, t]);

  useEffect(() => {
    if (shopLoading) return;
    load();
  }, [load, shopLoading]);

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    try {
      const track = await startJob({
        kind: 'rrpSkc',
        shopId,
        label: t('rrpOps.syncRrp'),
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      const finishedAt = new Date().toISOString();
      setLastSyncedAt(finishedAt);
      setPersistedSyncedAt(finishedAt);
      setQuery((prev) => ({ ...prev, _nonce: prev._nonce + 1 }));
      setPage(1);
      await refreshHomePending();
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      if (e?.message) showToast(e.message, 'error');
    }
  }, [shopId, startJob, onTrackDone, refreshHomePending, t]);

  const { refresh: guardedSync, refreshing: syncingLocal, cooldownSeconds: syncCooldown } = useGuardedRefresh(
    handleSync,
    { minIntervalMs: 3000 },
  );
  const syncing = syncingLocal || isBusy('rrpSkc', shopId);
  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const lastSyncedLabel = syncing
    ? t('productAction.syncing')
    : (latestSyncedAt
      ? t('rrpOps.lastSync', { time: formatSiteListSyncedAt(latestSyncedAt), count: total })
      : (total > 0 ? t('rrpOps.needPullShein', { count: total }) : t('rrpOps.notSyncedShein')));

  const listTabs = useMemo(() => stackShopDimensionTabs(shopDimensionTabs, (
    <div className="spo-status-tabs" role="tablist" aria-label={t('rrpOps.rrpStatusAria')}>
      {SKC_STATUS_TABS.map((tab) => (
        <button
          key={tab.value || 'all'}
          type="button"
          role="tab"
          aria-selected={String(statusFilter) === String(tab.value)}
          className={`spo-status-tab${String(statusFilter) === String(tab.value) ? ' spo-status-tab--active' : ''}`}
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            if (tab.value === '') next.delete('status');
            else next.set('status', tab.value);
            next.delete('audit');
            next.delete('view');
            setSearchParams(next, { replace: true });
            setPage(1);
          }}
        >
          {tab.value === '' ? t('tiktokCatalog.all') : t('rrpOps.shein' + tab.value, { defaultValue: tab.label })}
          {String(statusFilter) === String(tab.value) && total > 0 ? (
            <span className="spo-status-tab__count">{total}</span>
          ) : null}
        </button>
      ))}
    </div>
  )), [shopDimensionTabs, statusFilter, searchParams, setSearchParams, total, t]);

  const rowKey = (row) => String(row.skc_name || row.id || '');
  const selectableKeys = useMemo(() => rows.map(rowKey).filter(Boolean), [rows]);
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selectedKeys.has(k));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key, row) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        if (row) selectedRowsRef.current.set(key, row);
      }
      return next;
    });
  };

  useEffect(() => {
    rows.forEach((row) => {
      const key = rowKey(row);
      if (selectedKeys.has(key)) selectedRowsRef.current.set(key, row);
    });
  }, [rows, selectedKeys]);

  const onQuery = () => {
    setQuery({
      skc: filterDraft.skc,
      _nonce: query._nonce + 1,
    });
    setPage(1);
  };

  const onReset = () => {
    setFilterDraft({ skc: '' });
    setQuery({ skc: '', _nonce: query._nonce + 1 });
    setPage(1);
  };

  const batchModalMeta = {
    skc: { title: t('rrpOps.batchSkcTitle'), placeholder: t('rrpOps.batchSkcPh') },
  }[batchModalField] || { title: t('tiktokCatalog.batchQuery'), placeholder: t('rrpOps.batchLinePh') };

  const openEditBySkc = async (skcName, listRow = null) => {
    const skc = parseBatchTokens(skcName)[0] || String(skcName || '').trim();
    if (!shopId || !skc) {
      showToast(t('rrpOps.needSkc'), 'warning');
      return;
    }
    const session = ++editSessionRef.current;
    const isDemo = isRrpDemoSkc(skc);
    try {
      setSaving(true);

      // A+B：优先本地缓存/列表行秒开；Demo 永不打 live；真实 SKC 后台 live 补齐
      let apiRow = null;
      if (listRow && typeof listRow === 'object' && pickRrpSkcName(listRow) === skc) {
        apiRow = listRow;
      }

      try {
        const cachedRes = await sheinPlatformAPI.searchRecommendRetailPrice(shopId, {
          source: 'cached',
          page_num: 1,
          page_size: 1,
          skc_name_list: [skc],
        });
        if (session !== editSessionRef.current) return;
        const cachedRow = extractRrpSkcList(cachedRes)[0] || null;
        if (cachedRow) {
          apiRow = mergeRrpSkcEditRow(cachedRow, apiRow || listRow);
        }
      } catch {
        // 缓存失败时仍可用列表行
      }

      let openedFromLiveOnly = false;
      if (!apiRow) {
        if (isDemo) {
          showToast(t('rrpOps.demoMissing'), 'warning');
          return;
        }
        // 本地无数据时才阻塞等待平台（首次/未同步）
        try {
          const liveRes = await sheinPlatformAPI.searchRecommendRetailPrice(shopId, {
            source: 'live',
            skc_name_list: [skc],
          });
          if (session !== editSessionRef.current) return;
          apiRow = extractRrpSkcList(liveRes)[0] || null;
        } catch (err) {
          showToast(err?.message || t('rrpOps.queryOpenFailed'), 'error');
          return;
        }
        if (!apiRow) {
          showToast(t('rrpOps.skcMissing'), 'warning');
          return;
        }
        openedFromLiveOnly = true;
      }

      const merged = mergeRrpSkcEditRow(apiRow, listRow);
      const brand = pickRrpBrandCode(merged);
      const ruleRes = await sheinPlatformAPI.queryRecommendRetailPriceRule(
        shopId,
        brand ? { brand_code: brand } : {},
      );
      if (session !== editSessionRef.current) return;
      setRule(ruleRes?.data || ruleRes || null);
      setEditRow(merged);

      if (isDemo) {
        showToast(t('rrpOps.openedDemo'), 'success');
        return;
      }
      if (openedFromLiveOnly) {
        showToast(t('rrpOps.openedLive'), 'success');
        return;
      }

      showToast(t('rrpOps.openedCache'), 'success');

      // 后台 live：不阻塞编辑；允许用缓存直接提交
      void (async () => {
        try {
          const liveRes = await sheinPlatformAPI.searchRecommendRetailPrice(shopId, {
            source: 'live',
            skc_name_list: [skc],
          });
          if (session !== editSessionRef.current) return;
          const liveRow = extractRrpSkcList(liveRes)[0] || null;
          if (!liveRow) return;

          const nextMerged = mergeRrpSkcEditRow(liveRow, merged);
          setEditRow((prev) => {
            if (!prev || pickRrpSkcName(prev) !== skc) return prev;
            return mergeRrpSkcEditRow(liveRow, prev);
          });

          const liveBrand = pickRrpBrandCode(nextMerged);
          if (liveBrand && liveBrand !== brand) {
            try {
              const liveRuleRes = await sheinPlatformAPI.queryRecommendRetailPriceRule(
                shopId,
                { brand_code: liveBrand },
              );
              if (session !== editSessionRef.current) return;
              setRule(liveRuleRes?.data || liveRuleRes || null);
            } catch {
              // 规则刷新失败不打断编辑
            }
          }
          showToast(t('rrpOps.syncedLive'), 'success');
        } catch {
          // 后台同步失败静默，用户可继续用缓存提交
        }
      })();
    } catch (e) {
      if (session === editSessionRef.current) {
        setEditRow(null);
        setRule(null);
        showToast(e?.message || t('rrpOps.queryOpenFailed'), 'error');
      }
    } finally {
      if (session === editSessionRef.current) {
        setSaving(false);
      }
    }
  };

  /** 处理后：拉审核记录拿最新审核态，再回查 search 刷新本地当前价状态 */
  const refreshAfterProcess = async (skcNames = []) => {
    const skcs = (Array.isArray(skcNames) ? skcNames : [])
      .map((s) => String(s || '').trim())
      .filter(Boolean);
    for (const skc of skcs.slice(0, 10)) {
      try {
        await sheinPlatformAPI.searchRecommendRetailPriceAudit(shopId, {
          skc,
          page_num: 1,
          page_size: 20,
        });
      } catch {
        /* 审核明细失败不阻断；当前价仍以 search 回写为准 */
      }
    }
    await load();
    await refreshHomePending();
  };

  const openAuditDetail = async (skcName, listRow = null) => {
    const skc = String(skcName || '').trim();
    if (!shopId || !skc) return;
    try {
      setSaving(true);
      const res = await sheinPlatformAPI.searchRecommendRetailPriceAudit(shopId, {
        skc,
        page_num: 1,
        page_size: 50,
      });
      const list = extractRrpAuditList(res);
      setAuditDetail({ skc, rows: list, listRow: listRow || null });
      if (!list.length) {
        showToast(t('rrpOps.noAuditSkc'), 'info');
      }
    } catch (e) {
      showToast(e?.message || t('rrpOps.auditQueryFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (payload) => {
    if (!shopId) return;
    setSaving(true);
    try {
      const brandCode = payload.brandCode;
      delete payload.brandCode;
      if (brandCode) {
        await sheinPlatformAPI.queryRecommendRetailPriceRule(shopId, { brand_code: brandCode }).catch(() => null);
      }
      const res = await sheinPlatformAPI.batchSaveRecommendRetailPrice(shopId, payload);
      const failed = Array.isArray(res?.failed_list) ? res.failed_list : [];
      const success = Array.isArray(res?.success_list) ? res.success_list : [];
      if (failed.length && !success.length) {
        showToast(failed[0]?.reason || t('rrpOps.submitFailed'), 'error');
      } else {
        showToast(success.length ? t('rrpOps.submittedSkc', { count: success.length }) : (res?.msg || t('rrpOps.submitted')), 'success');
        const skcs = (payload.skc_rrp_list || []).map((r) => r?.skc_name).filter(Boolean);
        setEditRow(null);
        await refreshAfterProcess(skcs);
      }
    } catch (e) {
      showToast(e?.message || t('rrpOps.submitRrpFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const exportRows = (source) => {
    if (!source.length) {
      showToast(t('rrpOps.noneExport'), 'warning');
      return;
    }
    downloadCsv(
      `shein-rrp-skc-${Date.now()}.csv`,
      ['skc_name', 'status', 'status_desc', 'start_effective_time', 'end_effective_time', 'latest_update_time'],
      source.map((row) => ({
        skc_name: row.skc_name || '',
        status: rrpSkcStatusLabel(row.status),
        status_desc: row.status_desc || '',
        start_effective_time: formatMsTime(row.start_effective_time),
        end_effective_time: formatMsTime(row.end_effective_time),
        latest_update_time: formatMsTime(row.latest_update_time || row.last_update_time),
      })),
    );
    showToast(t('tiktokCatalog.exported', { count: source.length }), 'success');
  };

  const exportFiltered = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, size) => {
        const skcTokens = parseBatchTokens(query.skc).slice(0, RRP_BATCH_QUERY_LIMIT);
        const body = {
          source: 'cached',
          page_num: pageNo,
          page_size: size,
          skcNameList: skcTokens,
        };
        if (statusFilter !== '' && statusFilter != null) {
          body.status = Number(statusFilter);
        }
        const res = await sheinPlatformAPI.searchRecommendRetailPrice(shopId, body);
        const list = extractRrpSkcList(res);
        return { items: list, total: extractRrpSkcTotal(res, list.length) };
      });
      exportRows(all);
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const editSelected = () => {
    const firstKey = [...selectedKeys][0];
    const row = selectedRowsRef.current.get(firstKey) || rows.find((r) => rowKey(r) === firstKey);
    if (!row?.skc_name) {
      showToast(t('rrpOps.pickOne'), 'warning');
      return;
    }
    openEditBySkc(row.skc_name, row);
  };

  const queryChangePriceReasons = async () => {
    if (!shopId || !canDiscuss) {
      showToast(t('rrpOps.reasonScope'), 'warning');
      return;
    }
    const skcs = selectedKeys.size
      ? [...selectedKeys]
        .map((k) => selectedRowsRef.current.get(k)?.skc_name || rows.find((r) => rowKey(r) === k)?.skc_name)
        .filter(Boolean)
      : [String(filterDraft.skc || '').trim()].filter(Boolean);
    if (!skcs.length) {
      showToast(t('rrpOps.needSkcOrPick'), 'warning');
      return;
    }
    try {
      setSaving(true);
      const res = await sheinPlatformAPI.queryChangePriceReasons(shopId, skcs);
      const list = Array.isArray(res?.list) ? res.list : [];
      if (!list.length) {
        showToast(t('rrpOps.noReasons'), 'warning');
        return;
      }
      const msg = list.map((item) => {
        const reasons = Array.isArray(item?.changeReasonList) ? item.changeReasonList : [];
        const desc = reasons.map((r) => `${r.reasonCode}:${r.reasonDesc}`).join('；') || t('rrpOps.none');
        return `${item.skcName || item.skc_name || 'SKC'} → ${desc}`;
      }).join('\n');
      showToast(msg.slice(0, 500), 'info');
    } catch (e) {
      showToast(e?.message || t('rrpOps.reasonQueryFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const needsAuditDetail = (status) => {
    const n = Number(status);
    return n === 2 || n === 3 || n === 7 || n === 1;
  };

  const colSpan = 8;

  return (
    <>
      <SheinSyncProgressSlot kinds={['rrpSkc']} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || saving || syncing || exporting}
            syncKinds={['rrpSkc']}
            onSync={() => {
              if (!shopId) {
                showToast(t('returnList.pickShop'), 'warning');
                return;
              }
              guardedSync();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={lastSyncedLabel}
            syncHint={t('rrpOps.syncHintShein')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting || (!rows.length && selectedKeys.size === 0 && total <= 0)}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: () => exportRows(rows),
              onExportSelected: () => exportRows(
                [...selectedKeys].map((k) => selectedRowsRef.current.get(k)).filter(Boolean),
              ),
            })}
            extraActions={[
              {
                key: 'rrp-auto-rule',
                label: t('rrpOps.autoRule'),
                disabled: !shopId || loading || saving || syncing,
                onClick: () => setAutoRuleModalOpen(true),
              },
              {
                key: 'edit-selected',
                label: t('rrpOps.editSelected'),
                tone: 'primary',
                disabled: selectedKeys.size === 0 || saving,
                onClick: editSelected,
              },
              {
                key: 'query-edit-skc',
                label: saving ? t('rrpOps.querying') : t('rrpOps.editBySkc'),
                disabled: !shopId || saving || !String(filterDraft.skc || '').trim(),
                onClick: () => openEditBySkc(filterDraft.skc),
              },
              ...(canDiscuss ? [{
                key: 'change-price-reason',
                label: t('rrpOps.queryReasons'),
                disabled: !shopId || saving,
                onClick: queryChangePriceReasons,
              }] : []),
            ]}
          />
        )}
      >

        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline spo-field--batch spo-field--w300">
              <span className="spo-field__label">SKC</span>
              <input
                className="spo-input"
                value={filterDraft.skc}
                disabled={!shopId}
                placeholder={t('rrpOps.skcFilterPh')}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, skc: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={t('rrpOps.batchSkcAria')}
                title={t('tiktokCatalog.batchQuery')}
                disabled={!shopId}
                onClick={() => setBatchModalField('skc')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="spo-filter-bar__actions">
            <button
              type="button"
              className="spo-btn spo-btn--secondary spo-btn--sm"
              disabled={!shopId}
              onClick={onReset}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="spo-btn spo-btn--primary spo-btn--sm"
              disabled={!shopId || loading}
              onClick={onQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
        {error ? <div className="spo-ops-error" role="alert">{error}</div> : null}
      </ProductFilterCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalMeta.title}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalMeta.placeholder}
        onConfirm={(tokens) => {
          if (!batchModalField) return;
          const limited = tokens.slice(0, RRP_BATCH_QUERY_LIMIT);
          if (tokens.length > RRP_BATCH_QUERY_LIMIT) {
            showToast(t('rrpOps.batchLimit', { max: RRP_BATCH_QUERY_LIMIT }), 'warning');
          }
          setFilterDraft((prev) => ({ ...prev, [batchModalField]: joinBatchTokens(limited) }));
        }}
        onClose={() => setBatchModalField(null)}
      />

      <ProductListCard
        tabs={listTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('rrpOps.queryingRrp')}
      >
        <div className="spo-drafts__table-wrap spo-ops-table-wrap">
          <table className="spo-publish__table spo-drafts__table spo-ops-table spo-ops-table--price">
            <colgroup>
              <col className="spo-ops-col spo-ops-col--check shein-product-col--check" />
              <col className="spo-ops-col spo-ops-col--absorb" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--share" />
              <col className="spo-ops-col spo-ops-col--ops spo-ops-col--share" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-ops-th spo-ops-th--check shein-product-cell--spu-check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('rrpOps.selectAllRrp')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col">{t('rrpOps.productInfo')}</th>
                <th scope="col">{t('rrpOps.status')}</th>
                <th scope="col">{t('rrpOps.rrp')}</th>
                <th scope="col">{t('rrpOps.proofInfo')}</th>
                <th scope="col">{t('rrpOps.validity')}</th>
                <th scope="col">{t('rrpOps.updatedAt')}</th>
                <th scope="col" className="spo-ops-th--ops">{t('returnList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!shopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="spo-certs-empty-cell">
                    <div className="spo-certs-empty">
                      <p className="spo-certs-empty__title">
                        {!shopId
                          ? (allShopsScope ? t('draftBox.pickConcrete') : t('returnList.pickShop'))
                          : loading
                            ? t('returnList.loading')
                            : t('rrpOps.emptyRrp')}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const key = rowKey(row);
                  const title = pickRrpProductTitle(row);
                  const image = pickRrpProductImage(row);
                  const category = pickRrpCategory(row);
                  const priceSummary = summarizeSkuRrpInfoList(row.sku_rrp_info_list);
                  const proofInfo = collectRrpProofMaterials(row.sku_rrp_info_list);
                  return (
                    <tr key={key}>
                      <td
                        className="spo-ops-td spo-ops-td--check shein-product-cell--spu-check"
                        onClick={() => toggleOne(key, row)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('returnList.selectRow', { title: row.skc_name || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key, row)}
                        />
                      </td>
                      <td className="spo-ops-td spo-ops-td--product">
                        <div className="spo-return-product">
                          {image ? (
                            <SheinProductThumb
                              imageUrl={image}
                              className="spo-return-product__img"
                              placeholderClassName="spo-return-product__img spo-return-product__img--empty"
                              alt={title}
                            />
                          ) : (
                            <div className="spo-return-product__img spo-return-product__img--empty" />
                          )}
                          <div className="spo-return-product__meta">
                            <HoverCopyField
                              value={title || ''}
                              className="spo-return-product__title"
                              popoverCopy
                            />
                            <div className="spo-return-product__sku">SKC：{row.skc_name || '—'}</div>
                            <div className="spo-return-product__sku">
                              {t('rrpOps.categoryLine', { name: category || '—' })}
                              {row.sale_attribute_value ? ` · ${row.sale_attribute_value}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`spo-records__status spo-records__status--${rrpSkcStatusModifier(row.status)}`}>
                          {t('rrpOps.shein' + Number(row.status), { defaultValue: rrpSkcStatusLabel(row.status) })}
                        </span>
                      </td>
                      <td className="spo-rrp-price-cell" title={priceSummary.text}>
                        {priceSummary.lines?.length ? (
                          <div className="spo-rrp-price-lines">
                            {priceSummary.lines.map((line, idx) => (
                              <div key={`${idx}-${line}`} className="spo-rrp-price-lines__item">{line}</div>
                            ))}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="spo-rrp-proof-cell">
                        {proofInfo.hasProof ? (
                          <button
                            type="button"
                            className="spo-rrp-proof-view-btn"
                            onClick={() =>
                              setProofModal({
                                skc: row.skc_name || '',
                                items: proofInfo.items,
                              })
                            }
                          >
                            {t('rrpOps.viewProof')}
                          </button>
                        ) : (
                          <span className="spo-muted">{t('rrpOps.none')}</span>
                        )}
                      </td>
                      <td className="spo-rrp-validity-cell">
                        <div className="spo-rrp-validity">
                          <div className="spo-rrp-validity__line">
                            <span className="spo-rrp-validity__label">{t('rrpOps.start')}</span>
                            <span>{formatMsTime(row.start_effective_time)}</span>
                          </div>
                          <div className="spo-rrp-validity__line">
                            <span className="spo-rrp-validity__label">{t('rrpOps.end')}</span>
                            <span>{formatMsTime(row.end_effective_time)}</span>
                          </div>
                        </div>
                      </td>
                      <td>{formatMsTime(row.latest_update_time || row.last_update_time)}</td>
                      <td className="spo-certs-td spo-certs-td--ops spo-ops-td--ops">
                        <div className="spo-drafts__actions">
                          <button
                            type="button"
                            className="spo-btn spo-btn--primary spo-btn--sm"
                            disabled={!row.skc_name || saving}
                            onClick={() => openEditBySkc(row.skc_name, row)}
                          >
                            {t('productTemplate.edit')}
                          </button>
                          {needsAuditDetail(row.status) ? (
                            <button
                              type="button"
                              className="spo-btn spo-btn--secondary spo-btn--sm"
                              disabled={!row.skc_name || saving}
                              onClick={() => openAuditDetail(row.skc_name, row)}
                            >
                              {t('rrpOps.auditDetail')}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </ProductListCard>

      <RrpAutoRuleModal
        open={autoRuleModalOpen}
        shopId={shopId}
        busy={saving || syncing || loading}
        onClose={() => setAutoRuleModalOpen(false)}
        onRan={() => {
          load().catch(() => {});
        }}
      />

      <RrpEditModal
        open={Boolean(editRow)}
        shopId={shopId}
        skcRow={editRow}
        rule={rule}
        busy={saving}
        onClose={() => {
          if (saving) return;
          editSessionRef.current += 1;
          setEditRow(null);
          setRule(null);
        }}
        onSubmit={handleSave}
      />

      <RrpProofModal
        open={Boolean(proofModal)}
        skc={proofModal?.skc || ''}
        items={proofModal?.items || []}
        onClose={() => setProofModal(null)}
      />

      <RrpAuditDetailModal
        open={Boolean(auditDetail)}
        skc={auditDetail?.skc || ''}
        listRow={auditDetail?.listRow || null}
        rows={auditDetail?.rows || []}
        onClose={() => setAuditDetail(null)}
      />
    </>
  );
}
